import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  ComposedChart,
  Area,
} from "recharts";
import {
  Calculator,
  TrendingUp,
  DollarSign,
  Activity,
  Save,
  RefreshCw,
  Info,
  Clock,
  Target,
  Users,
  Zap,
  AlertTriangle,
  PieChart,
  TrendingDown,
  Wallet,
} from "lucide-react";

// --- HULPFUNCTIES & FORMATTERING ---

const formatCurrency = (val) => {
  if (val === Infinity || isNaN(val)) return "€ -";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(val);
};

const formatNumber = (val) => {
  if (val === Infinity || isNaN(val)) return "-";
  return new Intl.NumberFormat("nl-NL").format(val);
};

const formatPercent = (val) => {
  if (val === Infinity || isNaN(val)) return "-%";
  return val.toFixed(1) + "%";
};

// Helper voor Elasticiteit Labels in B2B taal
const getElasticityLabel = (val) => {
  if (val >= -0.8)
    return {
      label: "Zeer Loyaal / Uniek",
      desc: "Klanten accepteren prijsstijgingen (Lock-in/Niche).",
    };
  if (val >= -1.2)
    return {
      label: "Licht Gevoelig",
      desc: "Beperkt verloop bij prijsstijging.",
    };
  if (val >= -1.8)
    return {
      label: "Gemiddelde Markt",
      desc: "Normale reactie op prijsverandering.",
    };
  if (val >= -2.5)
    return {
      label: "Prijsgevoelig",
      desc: "Concurrentie is groot, klanten switchen snel.",
    };
  return {
    label: "Commodity (Vechtmarkt)",
    desc: "Prijs is allesbepalend. Hoog verloop.",
  };
};

// --- CORE REKENFUNCTIES ---

const calculateProfit = (p, interest, taxRate) => {
  const revenue = p.price * p.volume;
  const variableCosts = p.variableCost * p.volume;
  const contributionMargin = revenue - variableCosts;

  const ebitda = contributionMargin - p.fixedCost - p.marketingBudget;
  const ebit = ebitda - p.depreciation;

  const ebt = ebit - interest;
  const tax = Math.max(0, ebt * (taxRate / 100));
  return ebt - tax;
};

// CSV Export Functie
const exportForecastToCSV = (forecastData) => {
  if (!forecastData || forecastData.length === 0) return;

  const headers = ["Metriek", ...forecastData.map((f) => `Jaar ${f.year}`)];

  const rows = [
    { label: "Verkoopprijs", key: "price" },
    { label: "Afzet (Volume)", key: "volume" },
    { label: "NETTO OMZET", key: "revenue" },
    { label: "Variabele Kosten", key: "variableCosts" },
    { label: "DEKKINGSBIJDRAGE", key: "contributionMargin" },
    { label: "Vaste Kosten (Personeel/Algemeen)", key: "fixedCost" },
    { label: "Marketingbudget", key: "marketingBudget" },
    { label: "EBITDA", key: "ebitda" },
    { label: "Afschrijvingen", key: "depreciation" },
    { label: "EBIT", key: "ebit" },
    { label: "Rentekosten", key: "interest" },
    { label: "Winst voor Belasting (EBT)", key: "ebt" },
    { label: "Belastingen", key: "tax" },
    { label: "NETTO WINST (NPAT)", key: "npat" },
  ];

  const csvRows = rows.map((row) => {
    const values = forecastData.map((f) => {
      return f[row.key].toString().replace(".", ",");
    });
    return [row.label, ...values].join(";");
  });

  const csvContent = [headers.join(";"), ...csvRows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "Winstprognose_Export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// --- UI COMPONENTEN ---

const ControlSlider = ({
  label,
  value,
  onChange,
  min,
  max,
  step,
  prefix = "",
  suffix = "",
  helpText,
}) => (
  <div className="mb-5">
    <div className="flex justify-between items-center mb-1">
      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
        {label}
        {helpText && (
          <div className="group relative cursor-help">
            <Info size={14} className="text-slate-400" />
            <div className="hidden group-hover:block absolute left-0 bottom-6 w-56 bg-slate-800 text-white text-xs p-2 rounded shadow-lg z-50">
              {helpText}
            </div>
          </div>
        )}
      </label>
      <span className="text-sm font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded min-w-[60px] text-right">
        {prefix}
        {value.toLocaleString("nl-NL")}
        {suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600 hover:accent-blue-700 transition-colors"
    />
  </div>
);

const KPI = ({ title, value, subtext, icon: Icon, color = "blue" }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-2">
      <div className={`p-2 rounded-lg bg-${color}-50 text-${color}-600`}>
        <Icon size={20} />
      </div>
    </div>
    <div>
      <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wide">
        {title}
      </h3>
      <p
        className="text-xl font-bold text-slate-800 mt-1 truncate"
        title={typeof value === "string" ? value : ""}
      >
        {value}
      </p>
      {subtext && <p className="text-slate-400 text-xs mt-1">{subtext}</p>}
    </div>
  </div>
);

// --- HOOFDAPPLICATIE ---

const App = () => {
  // 1. STATE MANAGEMENT
  const defaultParams = {
    // Basis (Jaar 1)
    price: 1250,
    volume: 300,
    variableCost: 450,
    fixedCost: 150000,
    marketingBudget: 40000,
    depreciation: 5000,

    // Forecast
    years: 5,
    priceGrowth: 2.5,
    volumeGrowth: 5.0,
    costInflation: 2.0,

    // Financieel & B2B
    interestExpense: 2000,
    taxRate: 25,
    targetNetProfit: 200000,
    cac: 1200,
    churnRate: 10,

    // Elasticiteit (Start op gemiddeld -1.5)
    elasticity: -1.5,
  };

  const [params, setParams] = useState(defaultParams);
  const [activeTab, setActiveTab] = useState("dashboard");

  const handleParamChange = (key, val) =>
    setParams((prev) => ({ ...prev, [key]: val }));
  const handleReset = () => setParams(defaultParams);

  const marketScenario = getElasticityLabel(params.elasticity);

  // 2. CALCULATIE ENGINE (Memoized)
  const calculations = useMemo(() => {
    let forecast = [];
    let currentP = { ...params };

    // --- Loop voor Meerjaren Prognose ---
    for (let i = 1; i <= params.years; i++) {
      if (i > 1) {
        currentP.price *= 1 + params.priceGrowth / 100;
        currentP.volume *= 1 + params.volumeGrowth / 100;
        currentP.variableCost *= 1 + params.costInflation / 100;
        currentP.fixedCost *= 1 + params.costInflation / 100;
        currentP.marketingBudget *= 1 + params.costInflation / 100;
      }

      const revenue = currentP.price * currentP.volume;
      const variableCosts = currentP.variableCost * currentP.volume;
      const contributionMargin = revenue - variableCosts;

      const totalOpex = currentP.fixedCost + currentP.marketingBudget;
      const ebitda = contributionMargin - totalOpex;
      const ebit = ebitda - currentP.depreciation;
      const ebt = ebit - params.interestExpense;
      const tax = Math.max(0, ebt * (params.taxRate / 100));
      const npat = ebt - tax;

      const cmUnit = currentP.price - currentP.variableCost;
      const totalFixedExpenses =
        currentP.fixedCost + currentP.marketingBudget + currentP.depreciation;
      const breakEvenVol =
        cmUnit > 0 ? Math.ceil(totalFixedExpenses / cmUnit) : Infinity;

      forecast.push({
        year: i,
        price: currentP.price,
        volume: currentP.volume,
        variableCost: currentP.variableCost,
        fixedCost: currentP.fixedCost,
        marketingBudget: currentP.marketingBudget,
        depreciation: currentP.depreciation,
        revenue,
        variableCosts,
        contributionMargin,
        ebitda,
        ebit,
        interest: params.interestExpense,
        ebt,
        tax,
        npat,
        breakEvenVol,
        cmUnit,
      });
    }

    const year1 = forecast[0];

    // --- B2B / SaaS Metrics (Jaar 1) ---
    const churnDecimal = Math.max(0.01, params.churnRate / 100);
    const ltv = year1.cmUnit / churnDecimal;
    const ltvCacRatio = params.cac > 0 ? ltv / params.cac : 0;

    // --- Budget Reality Check ---
    const churnUnits = params.volume * (params.churnRate / 100);
    const growthUnits = params.volume * (params.volumeGrowth / 100);
    const totalNewUnitsNeeded = churnUnits + growthUnits;
    const impliedMarketingSpend = totalNewUnitsNeeded * params.cac;
    const budgetDeficit = impliedMarketingSpend - params.marketingBudget;

    // --- Goal Seeking (Jaar 1) ---
    const taxFactor = 1 - params.taxRate / 100;
    let requiredVolume = Infinity;

    if (year1.cmUnit > 0 && taxFactor > 0) {
      const requiredEBT = params.targetNetProfit / taxFactor;
      const requiredEBIT = requiredEBT + params.interestExpense;
      const requiredEBITDA = requiredEBIT + year1.depreciation;
      const requiredContribution =
        requiredEBITDA + year1.fixedCost + year1.marketingBudget;
      requiredVolume = Math.ceil(requiredContribution / year1.cmUnit);
    }

    // --- Break-even Chart Data (Jaar 1) ---
    const beChartData = [];
    const maxVol = Math.max(
      year1.volume * 1.5,
      year1.breakEvenVol !== Infinity
        ? year1.breakEvenVol * 1.5
        : year1.volume * 2
    );
    const steps = 20;

    for (let i = 0; i <= steps; i++) {
      const v = (maxVol / steps) * i;
      const omzet = v * year1.price;
      const varKosten = v * year1.variableCost;
      const vasteLasten =
        year1.fixedCost + year1.marketingBudget + year1.depreciation;

      beChartData.push({
        vol: v,
        omzet: omzet,
        kosten: vasteLasten + varKosten,
      });
    }

    // --- Prijsoptimalisatie Data ---
    const optChartData = [];
    const basePrice = params.price;
    const baseVolume = params.volume;
    const elasticity = params.elasticity;

    for (let pct = -40; pct <= 40; pct += 5) {
      const testPrice = basePrice * (1 + pct / 100);
      const pctVolChange = elasticity * (pct / 100);
      const testVolume = Math.max(0, baseVolume * (1 + pctVolChange));

      const pTest = {
        ...params,
        price: testPrice,
        volume: testVolume,
      };
      const profit = calculateProfit(
        pTest,
        params.interestExpense,
        params.taxRate
      );

      optChartData.push({
        pricePct: pct,
        price: testPrice,
        volume: testVolume,
        profit: profit,
        isCurrent: pct === 0,
      });
    }

    const optimalPoint = optChartData.reduce(
      (prev, current) => (prev.profit > current.profit ? prev : current),
      optChartData[0]
    );

    return {
      forecast,
      year1,
      ltv,
      ltvCacRatio,
      requiredVolume,
      beChartData,
      optChartData,
      optimalPoint,
      impliedMarketingSpend,
      budgetDeficit,
      totalNewUnitsNeeded,
    };
  }, [params]);

  // 3. GEVOELIGHEIDS ANALYSE
  const sensitivityData = useMemo(() => {
    const baseProfit = calculations.year1.npat;
    const drivers = [
      { key: "price", label: "Verkoopprijs", val: params.price },
      { key: "volume", label: "Afzet (Volume)", val: params.volume },
      {
        key: "variableCost",
        label: "Variabele Kosten",
        val: params.variableCost,
      },
      { key: "fixedCost", label: "Vaste Kosten (Alg.)", val: params.fixedCost },
      {
        key: "marketingBudget",
        label: "Marketingbudget",
        val: params.marketingBudget,
      },
    ];

    const results = drivers.map((d) => {
      const pPlus = { ...params, [d.key]: d.val * 1.1 };
      const profitPlus = calculateProfit(
        pPlus,
        params.interestExpense,
        params.taxRate
      );

      const pMinus = { ...params, [d.key]: d.val * 0.9 };
      const profitMinus = calculateProfit(
        pMinus,
        params.interestExpense,
        params.taxRate
      );

      const range = Math.abs(profitPlus - profitMinus);

      return {
        name: d.label,
        low: profitMinus,
        high: profitPlus,
        range: range,
        base: baseProfit,
        positiveCorrelation: profitPlus > profitMinus,
      };
    });

    return results.sort((a, b) => b.range - a.range);
  }, [params, calculations.year1]);

  // --- RENDERERS ---

  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          title="Netto Winst (NPAT)"
          value={formatCurrency(calculations.year1.npat)}
          icon={TrendingUp}
          color={calculations.year1.npat >= 0 ? "emerald" : "red"}
          subtext={`Marge: ${formatPercent(
            (calculations.year1.npat / calculations.year1.revenue) * 100
          )}`}
        />

        <KPI
          title="EBITDA"
          value={formatCurrency(calculations.year1.ebitda)}
          icon={PieChart}
          color="blue"
          subtext="Operationeel Resultaat"
        />

        <KPI
          title="Break-even Afzet"
          value={formatNumber(calculations.year1.breakEvenVol)}
          icon={Activity}
          color="indigo"
          subtext={`Huidig: ${formatPercent(
            (params.volume / calculations.year1.breakEvenVol) * 100
          )} van BE`}
        />

        <KPI
          title="LTV / CAC Ratio"
          value={calculations.ltvCacRatio.toFixed(2) + "x"}
          icon={Users}
          color={calculations.ltvCacRatio >= 3 ? "emerald" : "orange"}
          subtext={`LTV: ${formatCurrency(calculations.ltv)}`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
            <Activity size={18} className="text-blue-500" /> Break-even Analyse
            (Jaar 1)
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={calculations.beChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#e2e8f0"
                />
                <XAxis
                  dataKey="vol"
                  tickFormatter={(v) => formatNumber(v)}
                  label={{
                    value: "Afzet",
                    position: "insideBottomRight",
                    offset: -5,
                  }}
                  stroke="#94a3b8"
                />
                <YAxis
                  tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
                  stroke="#94a3b8"
                />
                <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                <Legend verticalAlign="top" height={36} />
                <Line
                  name="Omzet"
                  type="monotone"
                  dataKey="omzet"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={false}
                />
                <Line
                  name="Totale Kosten"
                  type="monotone"
                  dataKey="kosten"
                  stroke="#64748b"
                  strokeWidth={2}
                  dot={false}
                />
                {calculations.year1.breakEvenVol !== Infinity && (
                  <ReferenceLine
                    x={calculations.year1.breakEvenVol}
                    stroke="red"
                    strokeDasharray="3 3"
                    label="BE"
                  />
                )}
                <ReferenceLine
                  x={params.volume}
                  stroke="blue"
                  strokeDasharray="3 3"
                  label="Huidig"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* RECHTERKOLOM: Context Checks */}
        <div className="space-y-4">
          <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-2">
              <Target className="text-blue-600" size={20} />
              <h3 className="font-bold text-blue-800 text-sm">
                Winstdoel Check
              </h3>
            </div>

            <div className="bg-white p-3 rounded-lg shadow-sm mb-2 text-center">
              <span className="block text-[10px] text-slate-500 uppercase font-bold">
                Benodigde Afzet
              </span>
              <span className="text-2xl font-extrabold text-slate-800">
                {formatNumber(calculations.requiredVolume)}
              </span>
            </div>

            <div className="text-xs text-blue-600">
              {params.volume >= calculations.requiredVolume ? (
                <span className="flex items-center gap-1 font-bold text-emerald-600">
                  <TrendingUp size={14} /> Doel behaald!
                </span>
              ) : (
                <span className="flex items-center gap-1 font-bold text-orange-600">
                  <AlertTriangle size={14} /> Tekort:{" "}
                  {formatNumber(calculations.requiredVolume - params.volume)}
                </span>
              )}
            </div>
          </div>

          <div
            className={`p-6 rounded-xl border flex flex-col justify-center ${
              calculations.budgetDeficit > 0
                ? "bg-orange-50 border-orange-100"
                : "bg-emerald-50 border-emerald-100"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Wallet
                className={
                  calculations.budgetDeficit > 0
                    ? "text-orange-600"
                    : "text-emerald-600"
                }
                size={20}
              />
              <h3
                className={`font-bold text-sm ${
                  calculations.budgetDeficit > 0
                    ? "text-orange-800"
                    : "text-emerald-800"
                }`}
              >
                Budget Check
              </h3>
            </div>

            <p className="text-xs text-slate-600 mb-2">
              Om {formatPercent(params.volumeGrowth)} te groeien en{" "}
              {formatPercent(params.churnRate)} churn te vervangen (totaal{" "}
              {Math.round(calculations.totalNewUnitsNeeded)} nieuwe klanten), is
              dit budget nodig:
            </p>

            <div className="bg-white p-3 rounded-lg shadow-sm mb-2 text-center">
              <span className="block text-[10px] text-slate-500 uppercase font-bold">
                Benodigd Budget (via CAC)
              </span>
              <span
                className={`text-xl font-extrabold ${
                  calculations.budgetDeficit > 0
                    ? "text-orange-600"
                    : "text-emerald-600"
                }`}
              >
                {formatCurrency(calculations.impliedMarketingSpend)}
              </span>
            </div>

            <div className="text-xs">
              {calculations.budgetDeficit > 0 ? (
                <span className="flex items-center gap-1 font-bold text-orange-700">
                  <AlertTriangle size={14} /> Tekort:{" "}
                  {formatCurrency(calculations.budgetDeficit)}
                </span>
              ) : (
                <span className="flex items-center gap-1 font-bold text-emerald-700">
                  <TrendingUp size={14} /> Budget is toereikend
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderForecast = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-slate-700">Uitgebreide P&L (5 Jaar)</h3>
        <button
          onClick={() => exportForecastToCSV(calculations.forecast)}
          className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          <Save size={16} /> CSV Exporteren
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm text-right">
          <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">Omschrijving</th>
              {calculations.forecast.map((f) => (
                <th key={f.year} className="px-4 py-3">
                  Jaar {f.year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-slate-700">
            <tr className="hover:bg-slate-50 border-b border-slate-100">
              <td className="px-4 py-2 text-left font-medium">Netto Omzet</td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2">
                  {formatCurrency(f.revenue)}
                </td>
              ))}
            </tr>
            <tr className="hover:bg-slate-50 border-b border-slate-100 text-slate-500">
              <td className="px-4 py-2 text-left italic pl-8">
                Variabele Kosten
              </td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2 text-red-400">
                  -{formatCurrency(f.variableCosts)}
                </td>
              ))}
            </tr>
            <tr className="bg-blue-50 font-bold text-blue-800 border-b border-blue-100">
              <td className="px-4 py-2 text-left">Dekkingsbijdrage</td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2">
                  {formatCurrency(f.contributionMargin)}
                </td>
              ))}
            </tr>

            <tr className="hover:bg-slate-50 border-b border-slate-100 text-slate-500">
              <td className="px-4 py-2 text-left italic pl-8">
                Vaste Kosten (Pers./Alg.)
              </td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2 text-red-400">
                  -{formatCurrency(f.fixedCost)}
                </td>
              ))}
            </tr>
            <tr className="hover:bg-slate-50 border-b border-slate-100 text-slate-500">
              <td className="px-4 py-2 text-left italic pl-8">
                Marketingbudget
              </td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2 text-red-400">
                  -{formatCurrency(f.marketingBudget)}
                </td>
              ))}
            </tr>

            <tr className="font-bold text-slate-700 border-b border-slate-200 bg-slate-50">
              <td className="px-4 py-2 text-left">EBITDA</td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2">
                  {formatCurrency(f.ebitda)}
                </td>
              ))}
            </tr>

            <tr className="hover:bg-slate-50 border-b border-slate-100 text-slate-500">
              <td className="px-4 py-2 text-left italic pl-8">
                Afschrijvingen
              </td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2 text-red-400">
                  -{formatCurrency(f.depreciation)}
                </td>
              ))}
            </tr>

            <tr className="font-bold text-slate-800 border-b border-slate-200">
              <td className="px-4 py-2 text-left">EBIT (Bedrijfsresultaat)</td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2">
                  {formatCurrency(f.ebit)}
                </td>
              ))}
            </tr>

            <tr className="hover:bg-slate-50 border-b border-slate-100 text-slate-500">
              <td className="px-4 py-2 text-left italic pl-8">
                Rente & Belastingen
              </td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-2 text-red-400">
                  -{formatCurrency(f.interest + f.tax)}
                </td>
              ))}
            </tr>
            <tr className="bg-emerald-500 text-white font-bold text-base">
              <td className="px-4 py-3 text-left">NETTO WINST (NPAT)</td>
              {calculations.forecast.map((f) => (
                <td key={f.year} className="px-4 py-3">
                  {formatCurrency(f.npat)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSensitivity = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
              <TrendingDown size={18} className="text-purple-500" />{" "}
              Marktscenario Analyse
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Winstsimulatie bij prijsveranderingen, gebaseerd op uw{" "}
              <strong>markttype</strong>:
            </p>
            <div className="inline-block bg-purple-50 border border-purple-100 rounded px-3 py-2 text-sm text-purple-800 font-medium">
              {marketScenario.label}{" "}
              <span className="font-normal text-slate-500">
                {" "}
                — {marketScenario.desc}
              </span>
            </div>
          </div>
          <div className="bg-purple-50 p-2 rounded text-right">
            <span className="block text-xs text-purple-600 font-bold uppercase">
              Optimale Prijs
            </span>
            <span className="text-lg font-bold text-purple-800">
              {formatCurrency(calculations.optimalPoint.price)}
            </span>
          </div>
        </div>

        <div className="h-64 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={calculations.optChartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e2e8f0"
              />
              <XAxis
                dataKey="price"
                tickFormatter={(v) => `€${Math.round(v)}`}
                type="number"
                domain={["dataMin", "dataMax"]}
                label={{ value: "Prijs", position: "insideBottom", offset: -5 }}
              />
              <YAxis
                tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
                stroke="#94a3b8"
              />
              <RechartsTooltip
                labelFormatter={(v) => `Prijs: ${formatCurrency(v)}`}
                formatter={(v, name) => [
                  formatCurrency(v),
                  name === "profit" ? "Winst" : "Volume",
                ]}
              />
              <ReferenceLine
                x={params.price}
                stroke="blue"
                strokeDasharray="3 3"
                label="Huidig"
              />
              <Area
                type="monotone"
                dataKey="profit"
                name="Winst"
                stroke="#8b5cf6"
                fill="#8b5cf6"
                fillOpacity={0.1}
                strokeWidth={3}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-slate-400 mt-2 text-center">
          Visualiseert hoeveel volume u kunt verliezen bij prijsverhoging
          voordat de totale winst daalt.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
        <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
          <Zap size={18} className="text-orange-500" /> Tornado
          Gevoeligheidsanalyse
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          Impact op winst bij 10% verandering (Lineair model).
        </p>

        <div className="space-y-4">
          {sensitivityData.map((d, i) => (
            <div key={i} className="flex flex-col">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-bold text-slate-700">{d.name}</span>
                <span className="text-slate-400 text-xs">
                  Impact: {formatCurrency(d.range)}
                </span>
              </div>
              <div className="relative h-8 bg-slate-100 rounded overflow-hidden flex items-center justify-center">
                <div className="absolute w-0.5 h-full bg-slate-300 z-10 left-1/2"></div>

                <div
                  className={`absolute h-full right-1/2 border-r border-white ${
                    d.positiveCorrelation ? "bg-red-400" : "bg-emerald-400"
                  }`}
                  style={{
                    width: `${(Math.abs(d.base - d.low) / d.base) * 50}%`,
                    opacity: 0.8,
                  }}
                ></div>

                <div
                  className={`absolute h-full left-1/2 border-l border-white ${
                    d.positiveCorrelation ? "bg-emerald-400" : "bg-red-400"
                  }`}
                  style={{
                    width: `${(Math.abs(d.high - d.base) / d.base) * 50}%`,
                    opacity: 0.8,
                  }}
                ></div>

                <span className="absolute left-2 text-xs font-bold text-slate-600 z-20">
                  {d.positiveCorrelation ? "-10%" : "+10%"}
                </span>
                <span className="absolute right-2 text-xs font-bold text-slate-600 z-20">
                  {d.positiveCorrelation ? "+10%" : "-10%"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row h-screen bg-slate-50 font-sans text-slate-800 overflow-hidden">
      {/* SIDEBAR - STATISCH */}
      <div className="w-full md:w-80 bg-white border-b md:border-r border-slate-200 flex-shrink-0 flex flex-col h-1/3 md:h-full overflow-hidden shadow-sm z-20">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white flex-shrink-0">
          <div>
            <h1 className="font-bold text-base">Winstsimulator</h1>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest">
              Advanced P&L Model
            </p>
          </div>
          <button
            onClick={handleReset}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors"
            title="Reset Waarden"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-8">
          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 border-b pb-1">
              Omzet & Directe Kosten
            </h3>
            <ControlSlider
              label="Verkoopprijs"
              value={params.price}
              min={50}
              max={10000}
              step={50}
              prefix="€"
              onChange={(v) => handleParamChange("price", v)}
              helpText="Gemiddelde prijs per eenheid (Service/Product)."
            />
            <ControlSlider
              label="Volume (Afzet)"
              value={params.volume}
              min={10}
              max={5000}
              step={10}
              onChange={(v) => handleParamChange("volume", v)}
              helpText="Aantal verkochte eenheden."
            />
            <ControlSlider
              label="Variabele Kosten"
              value={params.variableCost}
              min={0}
              max={params.price * 0.9}
              step={10}
              prefix="€"
              onChange={(v) => handleParamChange("variableCost", v)}
              helpText="Directe kosten per eenheid (inkoop/productie)."
            />
          </section>

          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 border-b pb-1">
              Vaste Lasten & Marketing
            </h3>
            <ControlSlider
              label="Vaste Kosten (Alg.)"
              value={params.fixedCost}
              min={0}
              max={2000000}
              step={5000}
              prefix="€"
              onChange={(v) => handleParamChange("fixedCost", v)}
              helpText="Personeel, huur, software (Exclusief Marketing)."
            />
            <ControlSlider
              label="Marketingbudget"
              value={params.marketingBudget}
              min={0}
              max={500000}
              step={2500}
              prefix="€"
              onChange={(v) => handleParamChange("marketingBudget", v)}
              helpText="Jaarlijks budget voor campagnes/ads."
            />
            <ControlSlider
              label="Afschrijvingen"
              value={params.depreciation}
              min={0}
              max={250000}
              step={1000}
              prefix="€"
              onChange={(v) => handleParamChange("depreciation", v)}
              helpText="Jaarlijkse afschrijvingen (Depreciation)."
            />
          </section>

          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 border-b pb-1">
              Financieel & Groei
            </h3>
            <ControlSlider
              label="Rentelasten"
              value={params.interestExpense}
              min={0}
              max={100000}
              step={500}
              prefix="€"
              onChange={(v) => handleParamChange("interestExpense", v)}
            />
            <ControlSlider
              label="Belastingtarief"
              value={params.taxRate}
              min={0}
              max={50}
              step={1}
              suffix="%"
              onChange={(v) => handleParamChange("taxRate", v)}
            />
            <ControlSlider
              label="Doel: Netto Winst"
              value={params.targetNetProfit}
              min={0}
              max={1000000}
              step={10000}
              prefix="€"
              onChange={(v) => handleParamChange("targetNetProfit", v)}
            />
            <div className="h-px bg-slate-100 my-4"></div>
            <ControlSlider
              label="CAC"
              value={params.cac}
              min={50}
              max={5000}
              step={50}
              prefix="€"
              onChange={(v) => handleParamChange("cac", v)}
              helpText="Kosten om 1 nieuwe klant te werven (Customer Acquisition Cost)"
            />
            <ControlSlider
              label="Churn Rate"
              value={params.churnRate}
              min={1}
              max={50}
              step={1}
              suffix="%"
              onChange={(v) => handleParamChange("churnRate", v)}
              helpText="Jaarlijks percentage klantverloop"
            />
            <ControlSlider
              label="Volume Groei Prognose"
              value={params.volumeGrowth}
              min={-10}
              max={50}
              step={1}
              suffix="%"
              onChange={(v) => handleParamChange("volumeGrowth", v)}
              helpText="Verwachte jaarlijkse groei (gebruikt voor budget check)"
            />
          </section>

          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 border-b pb-1 text-purple-600">
              Marktdynamiek (B2B Realiteit)
            </h3>

            <div className="mb-2 p-2 bg-purple-50 rounded text-xs text-purple-800 border border-purple-100">
              <strong>Huidig Scenario:</strong> {marketScenario.label}
              <br />
              <span className="text-slate-500 italic">
                {marketScenario.desc}
              </span>
            </div>

            <div className="mb-5">
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium text-slate-700">
                  Marktgevoeligheid
                </label>
              </div>
              <input
                type="range"
                min={-3.0}
                max={-0.5}
                step={0.1}
                value={params.elasticity}
                onChange={(e) =>
                  handleParamChange("elasticity", Number(e.target.value))
                }
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-purple-600 hover:accent-purple-700 transition-colors"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-1">
                <span>Commodity</span>
                <span>Gemiddeld</span>
                <span>Uniek</span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col h-2/3 md:h-full overflow-hidden relative">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-slate-800 hidden md:block">
              Strategische Simulator
            </h2>
          </div>

          <div className="flex space-x-1 bg-slate-100 p-1 rounded-lg">
            {[
              { id: "dashboard", label: "Overzicht", icon: Calculator },
              { id: "forecast", label: "Prognose", icon: Clock },
              { id: "sensitivity", label: "Gevoeligheid", icon: Zap },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-3 py-2 text-xs md:text-sm font-medium rounded-md transition-all ${
                  activeTab === t.id
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <t.icon size={16} />{" "}
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            {activeTab === "dashboard" && renderDashboard()}
            {activeTab === "forecast" && renderForecast()}
            {activeTab === "sensitivity" && renderSensitivity()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
